import { useState, useEffect } from "react";
import { AuthAPI } from "../api";
import DateTimePicker from "./DateTimePicker";
import Dialog from "./Dialog";

// mentors: array for picker (date-clicks); lockedMentor: single object to display read-only (slot-clicks)
function RequestSessionModal({ isOpen, onClose, dateInfo, mentorId, mentors, lockedMentor }) {
  const [selectedMentorId, setSelectedMentorId] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState(20);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setDuration(20);
    setMessage("");
    setError("");
    onClose();
  };

  // Sync state from props whenever the modal opens
  useEffect(() => {
    if (isOpen && dateInfo) {
      setStartTime(new Date(dateInfo.date));
      setSelectedMentorId(mentorId != null ? String(mentorId) : "");
    }
  }, [isOpen, dateInfo, mentorId]);

  if (!isOpen || !dateInfo) return null;

  const showPicker = Array.isArray(mentors) && mentors.length > 0;
  const effectiveMentorId = showPicker ? selectedMentorId : (mentorId != null ? String(mentorId) : "");

  const start = startTime || new Date(dateInfo.date);
  const end = new Date(start.getTime() + duration * 60000);

  const handleSubmit = async () => {
    if (!effectiveMentorId) {
      setError("Please select a mentor before sending your request.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await AuthAPI.post("session-requests/", {
        mentor: effectiveMentorId,
        requested_start_time: start.toISOString(),
        requested_end_time: end.toISOString(),
        message,
      });
      setDuration(20);
      setMessage("");
      onClose();
    } catch (err) {
      console.error(err.response?.data || err);
      setError("Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
    >
      {({ titleId }) => (
        <div>
          <h3 id={titleId} className="mb-1 text-lg font-semibold text-gray-900">
            Request a session
          </h3>
          <p className="mb-5 text-sm text-gray-500">
            Your request will be sent to the mentor for approval.
          </p>

          {lockedMentor && !showPicker && (
            <div className="mb-4 rounded-xl border border-[#dde0f7] bg-[#eef0fa] px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#282E6A]">Mentor</p>
              <p className="text-sm font-medium text-[#0f1240]">
                {lockedMentor.profile.user.first_name} {lockedMentor.profile.user.last_name}
              </p>
              {lockedMentor.college_name && (
                <p className="mt-0.5 text-sm text-[#282E6A]">{lockedMentor.college_name}</p>
              )}
            </div>
          )}

          {showPicker && (
            <div className="mb-4">
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Mentor
              </label>
              <select
                value={selectedMentorId}
                onChange={(e) => { setSelectedMentorId(e.target.value); setError(""); }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
              >
                <option value="">Select a mentor…</option>
                {mentors.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.profile.user.first_name} {m.profile.user.last_name}
                    {m.college_name ? ` – ${m.college_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Start time
            </label>
            <DateTimePicker value={startTime} onChange={(date) => setStartTime(date)} />
          </div>

          <div className="mb-1.5">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
            >
              <option value={20}>20 minutes</option>
              <option value={40}>40 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>

          <p className="mb-5 text-xs text-gray-400">
            Session ends at{" "}
            <span className="font-medium text-gray-600">
              {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </p>

          <div className="mb-5">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Message to mentor <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              placeholder="e.g. I'd love to learn about your experience at MIT…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
            />
          </div>

          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A] ${
                loading
                  ? "cursor-not-allowed bg-gray-200 text-gray-400"
                  : "bg-[#282E6A] text-white hover:bg-[#1e2458]"
              }`}
              data-autofocus="true"
            >
              {loading ? "Sending…" : "Send request"}
            </button>
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default RequestSessionModal;
